import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ConfigurationField } from "../../types";

import { AsyncSelect } from "@/components/ui/v2/async-select";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import { fetchCaptainModels } from "@/features/captain/api/fetchCaptainModels";
import { fetchCockpitCandidateOrigins } from "@/features/recruitnow/candidate-origins/api/useCockpitCandidateOrigins";
import { SegmentSelect } from "@/features/segments/components/SegmentSelect";
import { FilterOption } from "@/types/FilterOption";

const getOptionValue = (o: FilterOption<string>) => o.value;
const getOptionLabel = (o: FilterOption<string>) => o.label;
const filterByLabel = (o: FilterOption<string>, query: string) =>
  o.label.toLowerCase().includes(query.toLowerCase());

export function ReferenceField({
  field,
  disabled,
  clientId,
  excludeIds,
  moduleName,
}: {
  field: Extract<ConfigurationField, { type: "reference" }>;
  disabled?: boolean;
  clientId?: string;
  excludeIds?: string[];
  moduleName?: string;
}) {
  const { control, setValue, trigger } = useFormContext();
  const { t } = useTranslation();

  const placeholder = field.placeholder?.startsWith("integrations.")
    ? t(field.placeholder)
    : field.placeholder || "Select an option";
  const referenceType = field.options["reference-type"];

  const isCandidateOrigins =
    referenceType.toLowerCase() === "candidateorigins";
  const isOpenRouterModel =
    referenceType.toLowerCase() === "openrouter-model";

  const fetcher = useCallback(async (): Promise<FilterOption<string>[]> => {
    if (isCandidateOrigins) {
      if (!clientId || !moduleName) return [];
      const origins = await fetchCockpitCandidateOrigins(clientId, moduleName);
      return origins.map((o) => ({ value: o.id, label: o.name ?? o.id }));
    }
    if (isOpenRouterModel) {
      const models = await fetchCaptainModels();
      return models.map((m) => ({ value: m.id, label: m.name }));
    }
    return [];
  }, [isCandidateOrigins, isOpenRouterModel, clientId, moduleName]);

  return (
    <FormField
      control={control}
      name={field.key}
      render={({ field: formField, fieldState }) => {
        if (referenceType.toLowerCase() === "segment") {
          return (
            <FormItem>
              <FormLabel>
                {t(field.label)}
                {field.required && (
                  <span className="text-destructive ml-1">*</span>
                )}
              </FormLabel>
              <FormControl>
                <SegmentSelect
                  clientId={clientId}
                  disabled={disabled}
                  fieldName={field.key}
                  placeholder={placeholder}
                  excludeSegmentIds={excludeIds}
                  aria-invalid={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }

        if (isCandidateOrigins || isOpenRouterModel) {
          return (
            <FormItem>
              <FormLabel>
                {t(field.label)}
                {field.required && (
                  <span className="text-destructive ml-1">*</span>
                )}
              </FormLabel>
              <FormControl>
                <AsyncSelect<FilterOption<string>>
                  preload
                  fetcher={fetcher}
                  filterFn={filterByLabel}
                  renderOption={getOptionLabel}
                  getOptionValue={getOptionValue}
                  getDisplayValue={getOptionLabel}
                  value={formField.value ?? ""}
                  onChange={(val) => {
                    setValue(field.key, val, {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                    trigger(field.key);
                  }}
                  placeholder={placeholder}
                  disabled={disabled}
                  width="100%"
                  noResultsMessage={t("commonSentences.noOptionsFound")}
                  clearAriaLabel={t("commonSentences.clearSelection")}
                  aria-invalid={!!fieldState.error}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }

        return (
          <FormItem>
            <FormLabel>
              {t(field.label)}
              {field.required && (
                <span className="text-destructive ml-1">*</span>
              )}
            </FormLabel>
            <FormControl>
              <Input
                type="text"
                placeholder={placeholder}
                disabled={disabled}
                value={formField.value ?? ""}
                onChange={formField.onChange}
                onBlur={formField.onBlur}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
